import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';
interface WebhookEvent {
  id: string;
  stripe_event_id: string;
  event_type: string;
  status: string;
  organization_id: string | null;
  created_at: string;
  processed_at: string | null;
  error_message: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_invoice_id: string | null;
  stripe_payment_intent_id: string | null;
  event_data: any;
}

interface WebhookStats {
  status_counts: Record<string, number>;
  event_type_counts: Record<string, number>;
  recent_failures: Array<{
    id: string;
    event_type: string;
    created_at: string;
    error_message: string;
    organization_id: string | null;
  }>;
  total_events: number;
}

const SuperAdminWebhooks: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<WebhookEvent | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    organization_id: '',
    event_type: '',
    status: '',
    stripe_customer_id: '',
  });

  useEffect(() => {
    fetchWebhookEvents();
    fetchWebhookStats();
  }, [filters]);

  const fetchWebhookEvents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.organization_id)
        params.append('organization_id', filters.organization_id);
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.stripe_customer_id)
        params.append('stripe_customer_id', filters.stripe_customer_id);

      const response = await axios.get(
        `${API_URL}/admin/billing/webhook-events?${params}`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        },
      );
      setEvents(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch webhook events');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookStats = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/billing/webhook-events/stats/summary`,
        {
          headers: {
            ...getAuthHeaders(),
          },
        },
      );
      setStats(response.data);
    } catch (err) {
      // console.error removed
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'processing':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ArrowPathIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user || user.role !== 'super_admin') {
    return <div>Access denied: Super admin privileges required</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Webhook Events</h1>
            <p className="text-gray-600 mt-2">
              Monitor and debug Stripe webhook events
            </p>
          </div>
          <button
            onClick={() => {
              fetchWebhookEvents();
              fetchWebhookStats();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <ArrowPathIcon className="w-5 h-5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Events</h3>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {stats.total_events.toLocaleString()}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Processed</h3>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {stats.status_counts.processed || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Processing</h3>
            <p className="text-2xl font-bold text-yellow-600 mt-1">
              {stats.status_counts.processing || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Failed</h3>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {stats.status_counts.failed || 0}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization ID
            </label>
            <input
              type="text"
              value={filters.organization_id}
              onChange={(e) =>
                setFilters({ ...filters, organization_id: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Filter by org ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Type
            </label>
            <select
              value={filters.event_type}
              onChange={(e) =>
                setFilters({ ...filters, event_type: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Events</option>
              <option value="checkout.session.completed">
                Checkout Completed
              </option>
              <option value="customer.subscription.created">
                Subscription Created
              </option>
              <option value="customer.subscription.updated">
                Subscription Updated
              </option>
              <option value="customer.subscription.deleted">
                Subscription Deleted
              </option>
              <option value="invoice.payment_succeeded">
                Payment Succeeded
              </option>
              <option value="invoice.payment_failed">Payment Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="received">Received</option>
              <option value="processing">Processing</option>
              <option value="processed">Processed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer ID
            </label>
            <input
              type="text"
              value={filters.stripe_customer_id}
              onChange={(e) =>
                setFilters({ ...filters, stripe_customer_id: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Filter by customer"
            />
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(event.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {event.event_type}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}
                      >
                        {getStatusIcon(event.status)}
                        <span className="ml-1">{event.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {event.organization_id ? (
                        <code className="text-xs">
                          {event.organization_id.substring(0, 8)}...
                        </code>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {event.stripe_customer_id ? (
                        <code className="text-xs">
                          {event.stripe_customer_id}
                        </code>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button
                        onClick={() => setSelectedEvent(event)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setSelectedEvent(null)}
            />
            <div className="relative bg-white rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-bold">Webhook Event Details</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    ID: {selectedEvent.stripe_event_id}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Event Type
                    </h3>
                    <p className="mt-1">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {selectedEvent.event_type}
                      </code>
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Status
                    </h3>
                    <p className="mt-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedEvent.status)}`}
                      >
                        {getStatusIcon(selectedEvent.status)}
                        <span className="ml-1">{selectedEvent.status}</span>
                      </span>
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Created At
                    </h3>
                    <p className="mt-1 text-sm">
                      {new Date(selectedEvent.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Processed At
                    </h3>
                    <p className="mt-1 text-sm">
                      {selectedEvent.processed_at
                        ? new Date(selectedEvent.processed_at).toLocaleString()
                        : '-'}
                    </p>
                  </div>
                </div>

                {selectedEvent.error_message && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <h3 className="text-sm font-medium text-red-800 mb-1">
                      Error Message
                    </h3>
                    <p className="text-sm text-red-700">
                      {selectedEvent.error_message}
                    </p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Event Data
                  </h3>
                  <pre className="bg-gray-50 p-4 rounded-md overflow-x-auto text-xs">
                    {JSON.stringify(selectedEvent.event_data, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminWebhooks;
